// MultiTabBookingForm.jsx - Clean Version with Original UI Preserved
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    Dialog, 
    Button, 
    Card, 
    Toast, 
    Dropdown, 
    Calendar, 
    InputText, 
    InputTextarea,
    DataTable, 
    Column,
    Steps, 
    Panel, 
    Divider, 
    Badge,
    Checkbox,
    Message,
    Tag,
    Rating,
    ProgressSpinner,
    Slider
} from 'primereact';
import useApi from "hooks/useApi";
import useAuth from "hooks/useAuth";
import useApp from "hooks/useApp";

const API_URL = process.env.REACT_APP_API_PATH;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_SGuxeIUsmygKAW';

const BookingQuickForm = ({ user }) => {
    const auth = useAuth();
    const app = useApp();
    const api = useApi();

    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [itemTypes, setItemTypes] = useState([]);
    const [filteredItemTypes, setFilteredItemTypes] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [partnerShops, setPartnerShops] = useState([]);
    const [filteredShops, setFilteredShops] = useState([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [estimatedPrice, setEstimatedPrice] = useState(0);
    const [errors, setErrors] = useState({});
    const [selectedShop, setSelectedShop] = useState(null);
    const [maxDistance, setMaxDistance] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [selectedShopType, setSelectedShopType] = useState(null);
    const [shopLayout, setShopLayout] = useState('grid');
    const [userLocation, setUserLocation] = useState(null);
    const [isLoadingShops, setIsLoadingShops] = useState(false);
    const [whatsappSending, setWhatsappSending] = useState(false);
    const [whatsappSent, setWhatsappSent] = useState(false);
    const [gettingPickupAddress, setGettingPickupAddress] = useState(false);
    const [gettingDeliveryAddress, setGettingDeliveryAddress] = useState(false);
    const [isLoadingTreatments, setIsLoadingTreatments] = useState(false);
    
    // Payment State
    const [paymentDialog, setPaymentDialog] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [paymentResponse, setPaymentResponse] = useState(null);
    const [createdBookingId, setCreatedBookingId] = useState(null);
    const [createdBookingReference, setCreatedBookingReference] = useState(null);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentOrderId, setPaymentOrderId] = useState(null);
    const [invoiceUrl, setInvoiceUrl] = useState(null);
    const [createdBookingPhone, setCreatedBookingPhone] = useState(null);

    // Helper function to get full address from user object
    const getUserFullAddress = (userData) => {
        if (!userData) return '';
        
        const addressParts = [];
        
        if (userData.address) addressParts.push(userData.address);
        if (userData.city) addressParts.push(userData.city);
        if (userData.state) addressParts.push(userData.state);
        if (userData.zipcode) addressParts.push(userData.zipcode);
        if (userData.zipCode) addressParts.push(userData.zipCode);
        
        return addressParts.filter(part => part && part.trim() !== '').join(', ');
    };

    // Booking Data State
    const [bookingData, setBookingData] = useState({
        ServiceId: null,
        Status: 'Confirmed',
        PickupDate: new Date(),
        DeliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
        PickupAddress: getUserFullAddress(user),
        DeliveryAddress: getUserFullAddress(user),
        SpecialInstructions: '',
        PreferredContactMethod: 'Phone',
        PaymentStatus: 'Pending',
        BaseAmount: 0,
        WeightCharge: 0,
        ExtraCharges: 0,
        DiscountAmount: 0,
        TaxRate: 0.18,
        TaxAmount: 0,
        TotalAmount: 0,
        IsUrgent: false,
        PriorityLevel: 1,
        IsRepeatCustomer: false,
        Source: 'Web',
        PartnerShopId: null,
        items: [
            { 
                id: 1, 
                itemtype: '', 
                quantity: 1, 
                treatment: '', 
                unitprice: 0,
                totalprice: 0,
                itemcategory: '',
                itemTypeId: null
            }
        ],
        acceptTerms: false
    });
    
    // Update addresses when user prop changes
useEffect(() => {
    if (user) {
        const fullAddress = getUserFullAddress(user);
        setBookingData(prev => ({
            ...prev,
            PickupAddress: fullAddress,
            DeliveryAddress: fullAddress
        }));
    }
}, [user]);

    const toast = useRef(null);
    const selectedService = services.find(s => s.id === bookingData.ServiceId);

    // Updated steps order: Service → Shop → Items → Details → Confirm
    const steps = [
        { label: 'Service', icon: 'pi pi-briefcase' },
        { label: 'Select Shop', icon: 'pi pi-book' },
        { label: 'Items', icon: 'pi pi-shopping-cart' },
        { label: 'Details', icon: 'pi pi-info-circle' },
        { label: 'Confirm', icon: 'pi pi-check' }
    ];

    const shopTypeOptions = [
        { label: 'All Types', value: null },
        { label: 'Laundry Only', value: 'Laundry' },
        { label: 'Dry Clean Only', value: 'DryClean' },
        { label: 'Both Services', value: 'Both' },
        { label: 'Premium', value: 'Premium' }
    ];

    // ==================== REVERSE GEOCODING FUNCTION ====================


    const getAddressFromCoords = async (latitude, longitude, addressType = 'pickup') => {
        const setter = addressType === 'pickup' ? setGettingPickupAddress : setGettingDeliveryAddress;
        setter(true);
        
        try {
            const response = await axios.get(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
                {
                    headers: {
                        'User-Agent': 'LaundryApp/1.0'
                    },
                    timeout: 10000
                }
            );
            
            if (response.data && response.data.display_name) {
                const address = response.data.display_name;
                
                if (addressType === 'pickup') {
                    setBookingData(prev => ({
                        ...prev,
                        PickupAddress: address
                    }));
                } else {
                    setBookingData(prev => ({
                        ...prev,
                        DeliveryAddress: address
                    }));
                }
                
                toast.current?.show({
                    severity: 'success',
                    summary: 'Location Found',
                    detail: `${addressType === 'pickup' ? 'Pickup' : 'Delivery'} address automatically set from your location`,
                    life: 3000
                });
                return address;
            }
            throw new Error('No address found');
        } catch (error) {
            console.error('Error getting address:', error);
            const fallbackAddress = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            
            if (addressType === 'pickup') {
                setBookingData(prev => ({
                    ...prev,
                    PickupAddress: fallbackAddress
                }));
            } else {
                setBookingData(prev => ({
                    ...prev,
                    DeliveryAddress: fallbackAddress
                }));
            }
            
            toast.current?.show({
                severity: 'warn',
                summary: 'Partial Location',
                detail: `Could not get full address for ${addressType}, using coordinates`,
                life: 3000
            });
            return fallbackAddress;
        } finally {
            setter(false);
        }
    };

    const getCurrentLocationAddress = (addressType = 'pickup') => {
        if (!navigator.geolocation) {
            toast.current?.show({
                severity: 'error',
                summary: 'Not Supported',
                detail: 'Geolocation is not supported by your browser',
                life: 3000
            });
            return;
        }

        const setter = addressType === 'pickup' ? setGettingPickupAddress : setGettingDeliveryAddress;
        setter(true);

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                await getAddressFromCoords(latitude, longitude, addressType);
            },
            (error) => {
                console.error('Geolocation error:', error);
                let errorMessage = 'Unable to get your location. ';
                
                switch(error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage += 'Please enable location access in your browser settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage += 'Location information is unavailable.';
                        break;
                    case error.TIMEOUT:
                        errorMessage += 'Location request timed out. Please try again.';
                        break;
                    default:
                        errorMessage += 'Please check your location settings and try again.';
                }
                
                toast.current?.show({
                    severity: 'error',
                    summary: 'Location Error',
                    detail: errorMessage,
                    life: 5000
                });
                setter(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0
            }
        );
    };

    // ==================== WHATSAPP FUNCTIONS ====================

    const formatDateTime = (date) => {
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

 const getItemTypeName = (itemType) => {
    const item = filteredItemTypes.find(it => 
        it.itemtype === itemType
    );
    return item?.itemtype || itemType || 'Unknown';
};

const getTreatmentName = (treatment) => {
    const treat = treatments.find(t => 
        t.treatment === treatment
    );
    return treat?.treatment || treatment || 'Standard';
};

    const generateVariables = () => {
        const totalItems = bookingData.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const itemsFormatted = bookingData.items.map(item => 
            `${item.quantity}x ${getItemTypeName(item.itemtype)}`
        ).join(' • ');
        
        let itemsSummary = itemsFormatted;
        if (itemsSummary.length > 40) {
            itemsSummary = itemsSummary.substring(0, 37) + '...';
        }

        const pickupDateTime = formatDateTime(bookingData.PickupDate);
        const deliveryDateTime = formatDateTime(bookingData.DeliveryDate);
        const pickupTime = pickupDateTime.includes(',') 
            ? pickupDateTime.split(',')[1]?.trim() 
            : pickupDateTime;
        const deliveryTime = deliveryDateTime.includes(',') 
            ? deliveryDateTime.split(',')[1]?.trim() 
            : deliveryDateTime;
        
        const pickupDateShort = bookingData.PickupDate 
            ? new Date(bookingData.PickupDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit' })
            : '';
        const deliveryDateShort = bookingData.DeliveryDate 
            ? new Date(bookingData.DeliveryDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit' })
            : '';
        const schedule = `${pickupDateShort} ${pickupTime} to ${deliveryDateShort} ${deliveryTime}`;

        const pickupAddress = bookingData.PickupAddress 
            ? (bookingData.PickupAddress.length > 30 
                ? bookingData.PickupAddress.substring(0, 27) + '...' 
                : bookingData.PickupAddress)
            : 'Address not available';

        const shopName = selectedShop?.ShopName || selectedShop?.shopName || 'Shop';
        const shopPhone = selectedShop?.ContactPhone || selectedShop?.contactPhone || '';
        const shopInfo = shopPhone 
            ? `${shopName} • ${shopPhone}`
            : shopName;

        return [
            createdBookingReference || 'N/A',
            schedule,
            `${selectedService?.name || 'Laundry'} • ${totalItems} items`,
            pickupAddress,
            shopInfo,
            `₹${estimatedPrice?.toFixed(2) || '0.00'}`,
            bookingData.IsUrgent ? 'URGENT' : '',
            bookingData.SpecialInstructions?.substring(0, 25) || ''
        ];
    };

    const sendWhatsAppInvoiceWithData = async (bookingId, bookingReference, customerPhone, amount) => {
        console.log('🔵 [WHATSAPP-WITH-DATA] Starting with:', { bookingId, bookingReference, customerPhone, amount });
        
        if (!customerPhone) {
            console.warn('No customer phone provided - skipping WhatsApp');
            return false;
        }

        if (!bookingId) {
            console.error('No booking ID provided');
            return false;
        }

        setWhatsappSending(true);
        
        try {
            let formattedPhone = customerPhone.replace(/\D/g, '');
            console.log('📱 Formatted phone (raw):', formattedPhone);
            
            if (formattedPhone.length === 10) {
                formattedPhone = `91${formattedPhone}`;
                console.log('📱 Added 91 prefix:', formattedPhone);
            } else if (formattedPhone.length === 11 && formattedPhone.startsWith('0')) {
                formattedPhone = `91${formattedPhone.substring(1)}`;
                console.log('📱 Removed leading 0 and added 91:', formattedPhone);
            }
            
            const payload = {
                bookingId: bookingId,
                customerPhone: formattedPhone,
                customerName: user?.name || user?.fullName || 'Customer',
                amount: amount,
                paymentId: bookingData.PaymentStatus === 'Paid' ? paymentResponse?.razorpay_payment_id || '' : ''
            };

            console.log('📤 Sending WhatsApp invoice payload:', payload);
            console.log('🌐 API URL:', `${API_URL}whats/send-invoice`);

            const response = await axios.post(
                `${API_URL}whats/send-invoice`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${auth.accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    timeout: 30000
                }
            );

            console.log('✅ WhatsApp API Response:', response.data);

            if (response.data && response.data.success) {
                setWhatsappSent(true);
                showSuccessMessage(response.data.message || 'WhatsApp confirmation sent successfully!');
                return true;
            } else {
                throw new Error(response.data?.message || 'Failed to send WhatsApp message');
            }
            
        } 
        
        
        catch (error) {
            console.error('Error sending WhatsApp message:', error);
            
            if (error.response) {
                console.error('Error response details:', {
                    status: error.response.status,
                    data: error.response.data
                });
            }
            
            return false;
            
        } finally {
            setWhatsappSending(false);
        }
    };

    // ==================== RAZORPAY FUNCTIONS ====================

    const createPaymentOrder = async (bookingId, amount) => {
        try {
            const response = await api.post(
                `${API_URL}payment/create-order`,
                {
                    bookingId: Number(bookingId),
                    amount: amount
                },
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    }
                }
            );

            if (!response.data.success) {
                throw new Error(response.data.message || "Order creation failed");
            }

            return response.data;

        } catch (error) {
            console.error("Create order error:", error);
            throw error;
        }
    };

    const initializeRazorpayPayment = async (bookingDetails) => {
        try {
            const orderData = await createPaymentOrder(
                bookingDetails.bookingId,
                bookingDetails.amount
            );

            if (!window.Razorpay) {
                alert("Razorpay SDK not loaded");
                return;
            }

            const options = {
                key: orderData.key,
                amount: orderData.amount,
                currency: "INR",
                name: "Laundry Service",
                description: `Booking #${bookingDetails.bookingId}`,
                order_id: orderData.orderId,
                handler: function (response) {
                    handlePaymentSuccess(response, bookingDetails);
                }
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();

        } catch (error) {
            console.error("Init payment error:", error);
            showError(error.message);
        }
    };

    const handlePaymentSuccess = async (response, bookingDetails) => {
        try {
            setPaymentLoading(true);

            const verifyResponse = await api.post(
                `${API_URL}payment/verify`,
                {
                    razorpay_order_id: response.razorpay_order_id,
                    razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                    bookingId: Number(bookingDetails.bookingId)
                },
                {
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`
                    }
                }
            );

            if (!verifyResponse.data?.success)
                throw new Error("Payment verification failed");

            setPaymentStatus("success");
            setPaymentResponse(response);

            showSuccessMessage("Payment Successful! 🎉");

            await updateBookingPaymentStatus(bookingDetails.bookingId, 'Paid', response.razorpay_payment_id);
            
            bookingData.PaymentStatus = 'Paid';
            
            const customerPhone = createdBookingPhone || user?.phone || user?.mobile;
            
            if (customerPhone) {
                await sendWhatsAppInvoiceWithData(
                    bookingDetails.bookingId,
                    bookingDetails.bookingReference,
                    customerPhone,
                    bookingDetails.amount
                );
            }

            setTimeout(() => {
                window.location.href = `/bookings/view/${bookingDetails.bookingId}`;
            }, 2000);

        } catch (error) {
            console.error("Verification error:", error);
            setPaymentStatus("failed");
            showError(error.response?.data?.message || "Verification failed");
        } finally {
            setPaymentLoading(false);
        }
    };

    const updateBookingPaymentStatus = async (bookingId, status, paymentId) => {
        try {
            await api.put(
                `${API_URL}bookings/${bookingId}/payment`,
                { paymentStatus: status, paymentId: paymentId },
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
            );
        } catch (error) {
            console.error('Error updating payment status:', error);
        }
    };

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => {
                resolve(true);
            };
            script.onerror = () => {
                resolve(false);
            };
            document.body.appendChild(script);
        });
    };

    // ==================== HELPER FUNCTIONS ====================

    const getUserLocationAsync = () => {
        return new Promise((resolve) => {
            if (!navigator.geolocation) {
                resolve(null);
                return;
            }
            
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const location = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    resolve(location);
                },
                (error) => {
                    console.warn('Location access denied or error:', error);
                    resolve(null);
                },
                { timeout: 10000, enableHighAccuracy: true }
            );
        });
    };

    const getUserLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                (error) => {
                    console.warn('Location access denied or error:', error);
                    setUserLocation({ lat: 40.7128, lng: -74.0060 });
                },
                { timeout: 10000, enableHighAccuracy: true }
            );
        } else {
            console.warn('Geolocation not supported by browser');
            setUserLocation({ lat: 40.7128, lng: -74.0060 });
        }
    };

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        if (!lat1 || !lon1 || !lat2 || !lon2) return 0;
        
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    };

    const getShopTypeSeverity = (type) => {
        switch (type) {
            case 'Premium': return 'success';
            case 'Both': return 'info';
            case 'DryClean': return 'warning';
            case 'Laundry': return null;
            default: return null;
        }
    };

    const getServiceIcon = (category) => {
        const iconMap = {
            'Express': 'pi-bolt',
            'Basic': 'pi-cloud',
            'Premium': 'pi-star',
            'Urgent': 'pi-exclamation-circle',
            'Standard': 'pi-check-circle',
            'default': 'pi-cloud'
        };
        return iconMap[category] || iconMap.default;
    };

    const getServiceColor = (category) => {
        const colorMap = {
            'Express': 'text-red-500',
            'Basic': 'text-blue-500',
            'Premium': 'text-purple-500',
            'Urgent': 'text-orange-500',
            'Standard': 'text-green-500',
            'default': 'text-gray-500'
        };
        return colorMap[category] || colorMap.default;
    };

    const showError = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 5000
            });
        }
    };

    const showSuccessMessage = (message) => {
        if (toast.current) {
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: message,
                life: 5000
            });
        }
    };

    // ==================== DATA LOADING ====================

    const initializeLocationAndLoad = async () => {
        try {
            const location = await getUserLocationAsync();
            if (location) {
                setUserLocation(location);
                console.log('📍 User location obtained:', location);
            } else {
                console.warn('⚠️ Could not get user location, will show all shops');
                setUserLocation({ lat: 40.7128, lng: -74.0060 });
            }
            
            await loadAllData();
            
        } catch (error) {
            console.error('Error in initialization:', error);
            await loadAllData();
        }
    };

    useEffect(() => {
        initializeLocationAndLoad();
        loadRazorpayScript();
    }, []);

    useEffect(() => {
        calculatePrice();
    }, [bookingData.ServiceId, bookingData.items, filteredItemTypes, treatments, services]);

    useEffect(() => {
        if (selectedShop) {
            const shopId = selectedShop.Id || selectedShop.id;
            
            setBookingData(prev => ({
                ...prev,
                PartnerShopId: shopId,
                DeliveryAddress: selectedShop.Address || selectedShop.address
            }));
            
            // Load item types for the selected shop
            loadItemTypesForShop(shopId);
            
            // Load treatments for the selected shop
            loadTreatments(shopId);
        }
    }, [selectedShop]);

    useEffect(() => {
        filterShops();
    }, [partnerShops, searchText, selectedShopType, maxDistance, userLocation]);

    // FIXED: Recalculate prices when treatments change
  useEffect(() => {
    // Recalculate all items when treatments change
    if (treatments.length > 0 && bookingData.items.length > 0) {
        const newItems = bookingData.items.map(item => {
            if (!item.itemtype) return item;
            
            const selectedItemType = filteredItemTypes.find(it => 
                it.itemtype === item.itemtype
            );
            const basePrice = selectedItemType?.baseprice || 0;
            const quantity = item.quantity || 1;
            // Use itemid from item types
            const itemTypeId = selectedItemType?.itemid;
            
            let finalPrice = basePrice;
            if (item.treatment && treatments.length > 0) {
                const treatmentObj = treatments.find(t => 
                    t.itemTypeId === itemTypeId && t.treatment === item.treatment
                );
                if (treatmentObj) {
                    const multiplier = parseFloat(treatmentObj.priceMultiplier || 1.0);
                    finalPrice = basePrice * multiplier;
                }
            }
            
            return {
                ...item,
                unitprice: parseFloat(finalPrice.toFixed(2)),
                totalprice: parseFloat((finalPrice * quantity).toFixed(2))
            };
        });
        
        setBookingData(prev => ({
            ...prev,
            items: newItems
        }));
    }
}, [treatments, filteredItemTypes]);


    const loadAllData = async () => {
        try {
            setLoading(true);
            await Promise.all([
                loadServices(),
                loadPartnerShops()
            ]);
        } catch (error) {
            console.error('Error loading data:', error);
            showError('Failed to load required data');
        } finally {
            setLoading(false);
        }
    };

    const loadServices = async () => {
        try {
            const response = await axios.get(`${API_URL}customer/service`, {
                timeout: 10000
            });
            const servicesData = response.data.records || response.data || [];
            setServices(servicesData);
            
            if (servicesData.length > 0 && !bookingData.ServiceId) {
                setBookingData(prev => ({
                    ...prev,
                    ServiceId: servicesData[0].id
                }));
            }
        } catch (error) {
            console.error('Error loading services:', error);
            showError('Failed to load services');
            setServices([
                { id: 1, name: 'Standard Laundry', baseprice: 25.00, description: 'Regular wash and fold service', Category: 'Basic', estimateddurationhours: 24 },
                { id: 2, name: 'Express Service', baseprice: 35.00, description: 'Fast 12-hour service', Category: 'Express', estimateddurationhours: 12 },
                { id: 3, name: 'Premium Care', baseprice: 50.00, description: 'Premium laundry with special care', Category: 'Premium', estimateddurationhours: 36 }
            ]);
        }
    };

  const loadItemTypesForShop = async (shopId) => {
    if (!shopId) {
        setFilteredItemTypes([]);
        return;
    }
    
    try {
        console.log(`Loading item types for shop ID: ${shopId}`);
        
        const response = await axios.get(`${API_URL}customer/itemtypes`, {
            params: { partnerShopId: shopId },
            timeout: 10000
        });
        
        const items = response.data.records || response.data || [];
        console.log(`Loaded ${items.length} item types for shop ${shopId}:`, items.map(item => ({
            itemid: item.itemid,  // Use itemid from the response
            name: item.itemtype,
            basePrice: item.baseprice
        })));
        
        setFilteredItemTypes(items);
        
        // Reset items if the selected shop changes
        if (items.length > 0) {
            const firstItem = items[0];
            // Use itemid from item types
            const itemTypeId = firstItem.itemid;
            const basePrice = firstItem.baseprice || 0;
            
            console.log('First item type ID (itemid):', itemTypeId);
            console.log('Current treatments:', treatments);
            
            // Find treatments for this item type - compare with itemTypeId
            const treatmentsForItem = treatments.filter(t => {
                return t.itemTypeId === itemTypeId;
            });
            
            console.log('Treatments for this item type:', treatmentsForItem);
            
            let defaultTreatment = '';
            let finalPrice = basePrice;
            
            if (treatmentsForItem.length > 0) {
                // Get unique treatments
                const uniqueTreatments = treatmentsForItem.reduce((acc, curr) => {
                    if (!acc.find(item => item.treatment === curr.treatment)) {
                        acc.push(curr);
                    }
                    return acc;
                }, []);
                
                if (uniqueTreatments.length > 0) {
                    const firstTreatment = uniqueTreatments[0];
                    defaultTreatment = firstTreatment.treatment || '';
                    const multiplier = parseFloat(firstTreatment.priceMultiplier || 1.0);
                    finalPrice = basePrice * multiplier;
                }
            }
            
            setBookingData(prev => ({
                ...prev,
                items: [{
                    id: Date.now(),
                    itemtype: firstItem.itemtype,
                    itemTypeId: itemTypeId,
                    quantity: 1,
                    treatment: defaultTreatment,
                    unitprice: parseFloat(finalPrice.toFixed(2)),
                    totalprice: parseFloat(finalPrice.toFixed(2)),
                    itemcategory: firstItem.category || ''
                }]
            }));
        } else {
            setBookingData(prev => ({
                ...prev,
                items: [{
                    id: Date.now(),
                    itemtype: '',
                    itemTypeId: null,
                    quantity: 1,
                    treatment: '',
                    unitprice: 0,
                    totalprice: 0,
                    itemcategory: ''
                }]
            }));
            showError('This shop does not have any items configured. Please select a different shop.');
        }
        
    } catch (error) {
        console.error('Error loading item types for shop:', error);
        showError('Failed to load items for selected shop');
        setFilteredItemTypes([]);
    }
};

    // FIXED: loadTreatments with correct API response structure
    const loadTreatments = async (shopId) => {
        if (!shopId) {
            console.warn('No shop ID provided for treatments');
            setTreatments([]);
            return;
        }

        setIsLoadingTreatments(true);
        
        try {
            console.log(`Loading treatments for shop ID: ${shopId}`);
            
            const response = await axios.get(`${API_URL}customer/treatments`, {
                params: { partnerShopId: shopId },
                timeout: 10000
            });
            
            console.log('Treatment API Response:', response.data);
            
            if (response.data.success) {
                const treatmentsData = response.data.records || [];
                console.log(`Loaded ${treatmentsData.length} treatments`);
                console.log('Sample treatment data:', treatmentsData.slice(0, 3));
                setTreatments(treatmentsData);
            } else {
                throw new Error(response.data.message || 'Failed to load treatments');
            }
        } catch (error) {
            console.error('Error loading treatments:', error);
            showError('Failed to load treatments');
            setTreatments([]);
        } finally {
            setIsLoadingTreatments(false);
        }
    };

    const loadPartnerShops = async () => {
        try {
            setIsLoadingShops(true);

            const params = new URLSearchParams({
                status: 'Active',
                isVerified: 'true',
                sortBy: 'rating',
                sortOrder: 'desc',
                pageSize: '50'
            });

            if (searchText) {
                params.append('search', searchText);
            }

            if (selectedShopType && selectedShopType !== 'All Types') {
                params.append('shopType', selectedShopType);
            }

            if (userLocation) {
                params.append('latitude', String(userLocation.lat));
                params.append('longitude', String(userLocation.lng));
                params.append('radius', String(maxDistance));
            }

            const response = await fetch(`${API_URL}partner/shops?${params}`, {
                method: "GET",
                headers: {
                    Accept: "application/json"
                }
            });

            const json = await response.json();

            if (json.success) {
                const shopsWithDistance = json.data.map(shop => {
                    const shopWithProps = {
                        ...shop,
                        Id: shop.id,
                        ShopName: shop.shopName,
                        OwnerName: shop.ownerName,
                        ContactPhone: shop.contactPhone,
                        ContactEmail: shop.contactEmail,
                        Address: shop.address,
                        City: shop.city,
                        State: shop.state,
                        ZipCode: shop.zipCode,
                        ShopType: shop.shopType,
                        Status: shop.status,
                        CommissionRate: shop.commissionRate,
                        ServiceRadius: shop.serviceRadius,
                        OperatingHours: shop.operatingHours,
                        CapacityPerDay: shop.capacityPerDay,
                        CurrentLoad: shop.currentLoad,
                        AvailableSlots: shop.availableSlots,
                        Rating: shop.rating,
                        TotalReviews: shop.totalReviews,
                        IsVerified: shop.isVerified,
                        VerificationDate: shop.verificationDate,
                        RegistrationDate: shop.registrationDate,
                        LastActive: shop.lastActive,
                        Notes: shop.notes,
                        TotalBookings: shop.totalBookings,
                        CompletedBookings: shop.completedBookings,
                        AvgRating: shop.avgRating,
                        OnTimeDeliveryRate: shop.onTimeDeliveryRate,
                        CustomerSatisfaction: shop.customerSatisfaction,
                        TodayAvailableSlots: shop.todayAvailableSlots,
                        IsAvailableToday: shop.isAvailableToday
                    };

                    if (userLocation && shop.latitude && shop.longitude) {
                        shopWithProps.distance = calculateDistance(
                            userLocation.lat,
                            userLocation.lng,
                            shop.latitude,
                            shop.longitude
                        );
                    } else {
                        shopWithProps.distance = 999;
                    }

                    return shopWithProps;
                });

                setPartnerShops(shopsWithDistance);
                
                let filtered = [...shopsWithDistance];
                
                if (userLocation) {
                    filtered = filtered.filter(shop => shop.distance <= maxDistance);
                }
                
                if (searchText) {
                    const searchLower = searchText.toLowerCase();
                    filtered = filtered.filter(shop =>
                        shop.ShopName?.toLowerCase().includes(searchLower) ||
                        shop.City?.toLowerCase().includes(searchLower) ||
                        shop.shopCode?.toLowerCase().includes(searchLower) ||
                        shop.OwnerName?.toLowerCase().includes(searchLower)
                    );
                }
                
                if (selectedShopType && selectedShopType !== 'All Types') {
                    filtered = filtered.filter(shop => shop.ShopType === selectedShopType);
                }
                
                filtered.sort((a, b) => a.distance - b.distance);
                
                setFilteredShops(filtered);
                
                if (filtered.length === 0 && userLocation && userLocation.lat !== 40.7128) {
                    toast.current?.show({
                        severity: 'info',
                        summary: 'No Shops Nearby',
                        detail: `No laundry shops found within ${maxDistance}km of your location. Try increasing the distance.`,
                        life: 5000
                    });
                }
            } else {
                console.error('Failed to load shops:', json.message);
                showError('Failed to load partner shops');
            }

        } catch (error) {
            console.error('Error loading partner shops:', error);
            showError('Error loading partner shops');
        } finally {
            setIsLoadingShops(false);
        }
    };

    const filterShops = () => {
        if (!partnerShops.length) return;

        let result = [...partnerShops];

        if (userLocation) {
            result = result.filter(shop => shop.distance <= maxDistance);
        }

        if (searchText) {
            const searchLower = searchText.toLowerCase();
            result = result.filter(shop =>
                shop.ShopName?.toLowerCase().includes(searchLower) ||
                shop.City?.toLowerCase().includes(searchLower) ||
                shop.shopCode?.toLowerCase().includes(searchLower) ||
                shop.OwnerName?.toLowerCase().includes(searchLower)
            );
        }

        if (selectedShopType && selectedShopType !== 'All Types') {
            result = result.filter(shop => shop.ShopType === selectedShopType);
        }

        result.sort((a, b) => a.distance - b.distance);

        setFilteredShops(result);
    };

    // ==================== PRICE CALCULATION ====================

    const calculatePrice = () => {
        let total = 0;
        let baseAmount = 0;
        
        if (selectedService) {
            baseAmount = selectedService.baseprice || selectedService.basePrice || 0;
            total += baseAmount;
        }
        
        bookingData.items.forEach((item) => {
            const itemPrice = item.unitprice || 0;
            const quantity = item.quantity || 1;
            total += itemPrice * quantity;
        });
        
        const taxRate = bookingData.TaxRate || 0.18;
        const taxAmount = total * taxRate;
        
        const discount = bookingData.DiscountAmount || 0;
        const finalTotal = Math.max(0, total + taxAmount - discount);
        
        setBookingData(prev => ({
            ...prev,
            BaseAmount: baseAmount,
            TotalAmount: finalTotal,
            TaxAmount: taxAmount
        }));
        
        setEstimatedPrice(finalTotal || 0);
    };

    const getItemPrice = (itemType, treatment) => {
        if (!itemType) return 0;
        
        const itemTypeObj = filteredItemTypes.find(it => 
            (it.Itemtype || it.itemtype) === itemType
        );
        
        if (!itemTypeObj) return 0;
        
        let basePrice = parseFloat(itemTypeObj.BasePrice || itemTypeObj.baseprice || 0);
        
        if (treatment && treatments.length > 0) {
            const treatmentObj = treatments.find(t => 
                t.treatment === treatment
            );
            
            if (treatmentObj) {
                const multiplier = parseFloat(treatmentObj.priceMultiplier || 1.0);
                basePrice *= multiplier;
            }
        }
        
        return basePrice;
    };

    // ==================== FORM HANDLING ====================

    const handleInputChange = (field, value) => {
        setBookingData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    // FIXED: handleArrayChange with proper treatment handling
  const handleArrayChange = (index, field, value) => {
    const newItems = [...bookingData.items];
    const updatedItem = { ...newItems[index] };
    
    const fieldName = field.toLowerCase();
    updatedItem[fieldName] = value;
    
    if (fieldName === 'itemtype') {
        const selectedItemType = filteredItemTypes.find(it => 
            it.itemtype === value
        );
        
        // Store the itemTypeId for filtering treatments - use itemid from item types
        const itemTypeId = selectedItemType?.itemid;
        updatedItem.itemTypeId = itemTypeId;
        
        const basePrice = selectedItemType?.baseprice || 0;
        const quantity = updatedItem.quantity || 1;
        
        const currentTreatment = updatedItem.treatment;
        let finalPrice = basePrice;
        
        if (currentTreatment && treatments.length > 0) {
            // Filter treatments by itemTypeId
            const treatmentObj = treatments.find(t => {
                return t.itemTypeId === itemTypeId && 
                       t.treatment === currentTreatment;
            });
            
            if (treatmentObj) {
                const multiplier = parseFloat(treatmentObj.priceMultiplier || 1.0);
                finalPrice = basePrice * multiplier;
                console.log(`Treatment multiplier applied: ${multiplier}, Base price: ${basePrice}, Final: ${finalPrice}`);
            } else {
                console.log('No treatment found for:', { itemTypeId, currentTreatment });
            }
        }
        
        updatedItem.unitprice = parseFloat(finalPrice.toFixed(2));
        updatedItem.totalprice = parseFloat((finalPrice * quantity).toFixed(2));
        updatedItem.itemcategory = selectedItemType?.category || '';
        
        // Reset treatment if it doesn't match the new item type
        if (updatedItem.treatment) {
            const treatmentExists = treatments.some(t => {
                return t.itemTypeId === itemTypeId && 
                       t.treatment === updatedItem.treatment;
            });
            
            if (!treatmentExists) {
                updatedItem.treatment = '';
                updatedItem.unitprice = parseFloat(basePrice.toFixed(2));
                updatedItem.totalprice = parseFloat((basePrice * quantity).toFixed(2));
            }
        }
    }
    
    if (fieldName === 'treatment') {
        const selectedItemType = filteredItemTypes.find(it => 
            it.itemtype === updatedItem.itemtype
        );
        const itemTypeId = selectedItemType?.itemid;
        const basePrice = selectedItemType?.baseprice || 0;
        const quantity = updatedItem.quantity || 1;
        
        let finalPrice = basePrice;
        
        if (value && treatments.length > 0) {
            // Find treatment that matches both itemTypeId and treatment name
            const treatmentObj = treatments.find(t => {
                return t.itemTypeId === itemTypeId && t.treatment === value;
            });
            
            if (treatmentObj) {
                const multiplier = parseFloat(treatmentObj.priceMultiplier || 1.0);
                finalPrice = basePrice * multiplier;
                console.log(`Treatment: ${value}, Base price: ${basePrice}, Multiplier: ${multiplier}, Final price: ${finalPrice}`);
            } else {
                console.warn('Treatment not found for item type:', itemTypeId, 'Treatment:', value);
                console.log('Available treatments for this item type:', 
                    treatments.filter(t => t.itemTypeId === itemTypeId).map(t => t.treatment)
                );
            }
        }
        
        updatedItem.unitprice = parseFloat(finalPrice.toFixed(2));
        updatedItem.totalprice = parseFloat((finalPrice * quantity).toFixed(2));
    }
    
    if (fieldName === 'quantity') {
        const unitPrice = updatedItem.unitprice || 0;
        const quantity = parseInt(value) || 1;
        updatedItem.totalprice = parseFloat((unitPrice * quantity).toFixed(2));
    }
    
    newItems[index] = updatedItem;
    
    setBookingData(prev => ({
        ...prev,
        items: newItems
    }));
};


    // FIXED: addItem with proper treatment handling
 const addItem = () => {
    if (filteredItemTypes.length === 0) {
        showError('No items available for this shop. Please select a shop first.');
        return;
    }
    
    if (treatments.length === 0 && !isLoadingTreatments) {
        showError('Treatments are not loaded. Please wait...');
        return;
    }
    
    const firstItem = filteredItemTypes[0];
    // Use itemid from item types
    const itemTypeId = firstItem.itemid;
    const basePrice = firstItem.baseprice || 0;
    
    console.log('First Item:', firstItem);
    console.log('Item Type ID (itemid):', itemTypeId);
    console.log('All Treatments:', treatments);
    
    // Find treatments for this item type - compare with itemTypeId
    const treatmentsForItem = treatments.filter(t => {
        return t.itemTypeId === itemTypeId;
    });
    
    console.log('Treatments for this item type:', treatmentsForItem);
    
    let defaultTreatment = '';
    let finalPrice = basePrice;
    
    if (treatmentsForItem.length > 0) {
        // Get unique treatments
        const uniqueTreatments = treatmentsForItem.reduce((acc, curr) => {
            if (!acc.find(item => item.treatment === curr.treatment)) {
                acc.push(curr);
            }
            return acc;
        }, []);
        
        if (uniqueTreatments.length > 0) {
            const firstTreatment = uniqueTreatments[0];
            defaultTreatment = firstTreatment.treatment || '';
            
            const multiplier = parseFloat(firstTreatment.priceMultiplier || 1.0);
            finalPrice = basePrice * multiplier;
            
            console.log('Adding item with treatment:', defaultTreatment, 'Multiplier:', multiplier, 'Final price:', finalPrice);
        }
    }
    
    const newItem = {
        id: Date.now(),
        itemtype: firstItem.itemtype,
        itemTypeId: itemTypeId,
        quantity: 1,
        treatment: defaultTreatment,
        unitprice: parseFloat(finalPrice.toFixed(2)),
        totalprice: parseFloat(finalPrice.toFixed(2)),
        itemcategory: firstItem.category || ''
    };
    
    setBookingData(prev => ({
        ...prev,
        items: [...prev.items, newItem]
    }));
};

    const removeItem = (id) => {
        if (bookingData.items.length <= 1) {
            showError('At least one item is required');
            return;
        }
        
        const newItems = bookingData.items.filter(item => item.id !== id);
        setBookingData(prev => ({
            ...prev,
            items: newItems
        }));
    };

    // ==================== VALIDATION ====================

    const validateStep = () => {
        const newErrors = {};
        
        switch(activeStep) {
            case 0:
                if (!bookingData.ServiceId) {
                    newErrors.ServiceId = 'Please select a service';
                }
                break;
                
            case 1:
                if (!selectedShop) {
                    newErrors.shop = 'Please select a partner shop';
                }
                break;
                
            case 2:
                if (bookingData.items.length === 0) {
                    newErrors.items = 'Please add at least one item';
                }
                if (!selectedShop) {
                    newErrors.shop = 'Please select a shop before adding items';
                }
                if (filteredItemTypes.length === 0 && selectedShop) {
                    newErrors.items = 'No items available for the selected shop';
                }
                bookingData.items.forEach((item, index) => {
                    if (!item.itemtype || item.itemtype.trim() === '') {
                        newErrors[`itemType_${index}`] = 'Item type is required';
                    }
                    if (!item.treatment || item.treatment.trim() === '') {
                        newErrors[`treatment_${index}`] = 'Treatment is required';
                    }
                    if (!item.quantity || item.quantity < 1) {
                        newErrors[`quantity_${index}`] = 'Quantity must be at least 1';
                    }
                });
                break;
                
            case 3:
                if (!bookingData.PickupDate) {
                    newErrors.PickupDate = 'Pickup date is required';
                }
                if (!bookingData.DeliveryDate) {
                    newErrors.DeliveryDate = 'Delivery date is required';
                }
                if (new Date(bookingData.DeliveryDate) < new Date(bookingData.PickupDate)) {
                    newErrors.DeliveryDate = 'Delivery must be after pickup';
                }
                if (!bookingData.PickupAddress?.trim()) {
                    newErrors.PickupAddress = 'Pickup address is required';
                }
                break;
                
            case 4:
                if (!bookingData.acceptTerms) {
                    newErrors.acceptTerms = 'You must accept the terms and conditions';
                }
                break;
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ==================== NAVIGATION ====================

    const handleNext = () => {
        if (validateStep()) {
            setActiveStep(prev => prev + 1);
        } else {
            showError('Please fix the errors before proceeding');
        }
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    // ==================== BOOKING SUBMISSION ====================

    const handleSubmit = async () => {
        console.log('🚀 [1] handleSubmit STARTED');
        
        if (!validateStep()) {
            showError('Please fix the errors before proceeding');
            return;
        }

        if (!auth?.userId) {
            showError('Please login to create a booking');
            return;
        }

        setLoading(true);
        try {
            const totalWeight = bookingData.items.reduce((sum, item) => {
                const itemTypeObj = filteredItemTypes.find(it => 
                    (it.Itemtype || it.itemtype) === item.itemtype
                );
                const itemWeight = itemTypeObj?.WeightRange === 'Heavy' ? 0.5 : 
                                  itemTypeObj?.WeightRange === 'Medium' ? 0.3 : 0.1;
                return sum + (itemWeight * (item.quantity || 1));
            }, 0);
            
            const totalItems = bookingData.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
            
            const bookingPayload = {
                UserId: Number(auth.userId),
                ServiceId: bookingData.ServiceId,
                PartnerShopId: bookingData.PartnerShopId,
                Status: bookingData.Status,
                PickupDate: bookingData.PickupDate.toISOString(),
                DeliveryDate: bookingData.DeliveryDate.toISOString(),
                PickupAddress: bookingData.PickupAddress,
                DeliveryAddress: bookingData.DeliveryAddress || bookingData.PickupAddress,
                SpecialInstructions: bookingData.SpecialInstructions,
                PreferredContactMethod: bookingData.PreferredContactMethod,
                PaymentStatus: 'Pending',
                BaseAmount: bookingData.BaseAmount,
                WeightCharge: bookingData.WeightCharge,
                ExtraCharges: bookingData.ExtraCharges,
                DiscountAmount: bookingData.DiscountAmount,
                TaxRate: bookingData.TaxRate,
                TaxAmount: bookingData.TaxAmount,
                TotalAmount: bookingData.TotalAmount,
                TotalWeight: totalWeight,
                TotalItems: totalItems,
                IsUrgent: bookingData.IsUrgent,
                PriorityLevel: bookingData.PriorityLevel,
                IsRepeatCustomer: bookingData.IsRepeatCustomer,
                Source: bookingData.Source,
                items: bookingData.items.map(item => ({
                    ItemType: item.itemtype,
                    Quantity: item.quantity,
                    Treatment: item.treatment,
                    UnitPrice: item.unitprice,
                    TotalPrice: item.totalprice,
                    ItemCategory: item.itemcategory,
                    Instructions: ''
                }))
            };

            console.log('📤 [2] Submitting booking payload:', bookingPayload);

            const response = await axios.post(`${API_URL}dashboard/create-booking`, bookingPayload, {
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.token}`
                },
                timeout: 15000
            });

            console.log('✅ [3] Booking API Response:', response.data);

            if (response.data.success) {
                console.log('Booking created:', response.data);
                showSuccessMessage(response.data.message);
            }

            const responseData = response.data;
            
            let bookingId = null;
            let bookingReference = null;
            let customerPhone = null;

            if (responseData) {
                bookingId = responseData.bookingId || responseData.Id || responseData.id || responseData.BookingId;
                bookingReference = responseData.bookingReference || responseData.BookingReference || responseData.reference;
                customerPhone = responseData.customerPhone;
                
                if (customerPhone) {
                    setCreatedBookingPhone(customerPhone);
                }
            
                if (!bookingId && responseData.data) {
                    bookingId = responseData.data.bookingId || responseData.data.id || responseData.data.BookingId;
                    bookingReference = responseData.data.bookingReference || responseData.data.BookingReference;
                    customerPhone = responseData.data.customerPhone;
                    if (customerPhone) setCreatedBookingPhone(customerPhone);
                }
                
                if (!bookingId && responseData.records && responseData.records.length > 0) {
                    bookingId = responseData.records[0].Id || responseData.records[0].id;
                    bookingReference = responseData.records[0].BookingReference || responseData.records[0].bookingReference;
                    customerPhone = responseData.records[0].customerPhone;
                    if (customerPhone) setCreatedBookingPhone(customerPhone);
                }
            }

            if (!bookingId) {
                console.error('❌ [4] Could not extract booking ID from response:', responseData);
                throw new Error('Failed to get booking ID from server response');
            }

            console.log('📝 [5] Extracted booking data:', {
                bookingId, 
                bookingReference,
                customerPhone,
                estimatedPrice
            });

            setCreatedBookingId(bookingId);
            setCreatedBookingReference(bookingReference || `BOOK${bookingId}`);
            sendWhatsAppNotification(bookingReference)
            .then(data => console.log('Success:', data))
            .catch(err => console.error('Error:', err.message));

            console.log('📢 [6] About to send WhatsApp invoice...');
            console.log('📞 Customer phone from response:', customerPhone);
            console.log('💰 Estimated price:', estimatedPrice);
            
            if (customerPhone) {
                try {
                    console.log('📢 [7] Calling sendWhatsAppInvoice...');
                    const whatsappResult = await sendWhatsAppInvoiceWithData(
                        bookingId, 
                        bookingReference, 
                        customerPhone, 
                        estimatedPrice
                    );
                    console.log('✅ [8] WhatsApp result:', whatsappResult);
                    
                    if (whatsappResult) {
                        console.log('✅ [9] WhatsApp sent successfully');
                    } else {
                        console.warn('⚠️ [9] WhatsApp returned false');
                    }
                } catch (whatsappError) {
                    console.error('❌ [9] WhatsApp error:', whatsappError);
                    showError('Booking created but WhatsApp confirmation failed');
                }
            } else {
                console.warn('⚠️ No customer phone found in response, skipping WhatsApp');
            }
            
            showSuccessMessage(`Booking #${bookingReference || bookingId} created successfully!`);
            
            setShowSuccess(true);
            console.log('✅ [10] handleSubmit COMPLETED successfully');

        } catch (error) {
            console.error('❌ [ERROR] Booking error:', error);
            
            if (error.response) {
                const { status, data } = error.response;
                
                if (status === 400) {
                    if (data.message && data.message.includes('Total amount must be at least')) {
                        showError(`Minimum Order Requirement: ${data.message}`);
                    } else {
                        showError(data.message || 'Invalid request. Please check your inputs.');
                    }
                } 
                else if (status === 500) {
                    showError('Server error. Please try again later.');
                    console.error('Server error details:', data);
                }
                else {
                    showError(data.message || 'An error occurred while creating the booking.');
                }
            } 
            else if (error.request) {
                showError('Network error. Please check your connection.');
            } 
            else {
                showError(error.message || 'An unexpected error occurred.');
            }
        } finally {
            setLoading(false);
            console.log('🏁 [11] handleSubmit FINISHED');
        }
    };


const sendWhatsAppNotification = async (bookingRef) => {
  const res = await fetch(`${API_URL}whats/notify-partner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ OrderRefId: bookingRef })
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message);
  return data;
};

    const proceedToPayment = () => {
        if (!createdBookingId) {
            showError('Booking ID is missing. Please try again or contact support.');
            setShowSuccess(false);
            return;
        }
        
        if (!estimatedPrice || estimatedPrice <= 0) {
            showError('Invalid amount. Please contact support.');
            setShowSuccess(false);
            return;
        }

        console.log('Proceeding to payment with:', {
            bookingId: createdBookingId,
            reference: createdBookingReference,
            amount: estimatedPrice
        });
        
        setShowSuccess(false);
        setPaymentDialog(true);
        
        setTimeout(() => {
            initializeRazorpayPayment({
                bookingId: createdBookingId,
                bookingReference: createdBookingReference || `BOOK${createdBookingId}`,
                amount: estimatedPrice
            });
        }, 1000);
    };

    const resetForm = () => {
        setBookingData({
            ServiceId: null,
            Status: 'Pending',
            PickupDate: new Date(),
            DeliveryDate: new Date(Date.now() + 24 * 60 * 60 * 1000),
            PickupAddress: user?.address || '',
            DeliveryAddress: user?.address || '',
            SpecialInstructions: '',
            PreferredContactMethod: 'Phone',
            PaymentStatus: 'Pending',
            BaseAmount: 0,
            WeightCharge: 0,
            ExtraCharges: 0,
            DiscountAmount: 0,
            TaxRate: 0.18,
            TaxAmount: 0,
            TotalAmount: 0,
            IsUrgent: false,
            PriorityLevel: 1,
            IsRepeatCustomer: false,
            Source: 'Web',
            PartnerShopId: null,
            items: [
                { 
                    id: 1, 
                    itemtype: '', 
                    quantity: 1, 
                    treatment: '', 
                    unitprice: 0,
                    totalprice: 0,
                    itemcategory: '',
                    itemTypeId: null
                }
            ],
            acceptTerms: false
        });
        setSelectedShop(null);
        setFilteredItemTypes([]);
        setTreatments([]);
        setActiveStep(0);
        setEstimatedPrice(0);
        setPaymentStatus(null);
        setPaymentResponse(null);
        setCreatedBookingId(null);
        setCreatedBookingReference(null);
        setPaymentOrderId(null);
        setWhatsappSent(false);
    };

    // ==================== SHOP CARD COMPONENTS ====================

    const shopGridItem = (shop) => {
        const isSelected = selectedShop?.Id === shop.Id || selectedShop?.id === shop.id;
        
        return (
            <div className="col-12 md:col-6 lg:col-4 p-2" key={shop.Id || shop.id}>
                <Card 
                    className={`cursor-pointer transition-all duration-200 h-full ${
                        isSelected ? 'border-2 border-primary shadow-3 bg-blue-50' : 'border-1 border-gray-200'
                    }`}
                    onClick={() => setSelectedShop(shop)}
                >
                    <div className="text-center relative">
                        {isSelected && (
                            <div className="absolute top-3 right-3">
                                <i className="pi pi-check-circle text-primary" style={{ fontSize: '1.5rem' }}></i>
                            </div>
                        )}
                        
                        <div className="flex align-items-center justify-content-between mb-2">
                            <div className="flex align-items-center gap-2">
                                <Tag 
                                    value={shop.ShopType} 
                                    severity={getShopTypeSeverity(shop.ShopType)}
                                    className="font-bold"
                                />
                                {shop.IsVerified && (
                                    <Tag value="Verified" severity="success" />
                                )}
                            </div>
                            {shop.distance > 0 && shop.distance < 999 && (
                                <div className="text-sm font-medium">
                                    <i className="pi pi-map-marker mr-1"></i>
                                    {shop.distance.toFixed(1)} km
                                </div>
                            )}
                        </div>
                        
                        <h4 className={`mt-2 mb-2 ${isSelected ? 'text-primary' : ''}`}>
                            {shop.ShopName || shop.shopName}
                        </h4>
                        
                        <p className="text-sm text-gray-600 mb-2">
                            <i className="pi pi-map-marker mr-1"></i>
                            {shop.City || shop.city}, {shop.State || shop.state}
                        </p>
                        
                        <div className="flex align-items-center justify-content-center mb-3">
                            <Rating 
                                value={shop.Rating || shop.rating || 0} 
                                readOnly 
                                cancel={false} 
                                stars={5} 
                            />
                            <span className="ml-2 text-sm font-semibold">
                                {(shop.Rating || shop.rating || 0).toFixed(1)} ({shop.TotalReviews || shop.totalReviews || 0} reviews)
                            </span>
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-3">
                            <i className="pi pi-user mr-1"></i>
                            {shop.OwnerName || shop.ownerName}
                        </div>
                        
                        <div className="text-sm text-gray-600 mb-3">
                            <i className="pi pi-phone mr-1"></i>
                            {shop.ContactPhone || shop.contactPhone}
                        </div>
                        
                        <div className="flex justify-content-between text-sm mb-3">
                            <div>
                                <span className="font-bold">{shop.AvailableSlots || shop.availableSlots || 0}</span>
                                <div className="text-xs text-gray-500">Available Slots</div>
                            </div>
                            <div>
                                <span className="font-bold">₹{shop.min_order_amt || shop.min_order_amt || 0} </span>
                                <div className="text-xs text-gray-500">Min Order Amount</div>
                            </div>
                            <div>
                                <span className="font-bold">{shop.ServiceRadius || shop.serviceRadius || 0}km</span>
                                <div className="text-xs text-gray-500">Radius</div>
                            </div>
                        </div>
                        
                        <div className="mt-3">
                            <Button 
                                label={isSelected ? "Selected" : "Select Shop"}
                                icon={isSelected ? "pi pi-check" : "pi pi-arrow-right"}
                                className={isSelected ? 
                                    "p-button-success p-button-sm w-full" : 
                                    "p-button-outlined p-button-sm w-full"
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedShop(shop);
                                }}
                            />
                        </div>
                    </div>
                </Card>
            </div>
        );
    };

    const shopListItem = (shop) => {
        const isSelected = selectedShop?.Id === shop.Id || selectedShop?.id === shop.id;
        
        return (
            <div className="col-12 mb-3" key={shop.Id || shop.id}>
                <Card className={isSelected ? 'border-2 border-primary bg-blue-50' : ''}>
                    <div className="flex flex-column md:flex-row align-items-start md:align-items-center">
                        <div className="w-full md:w-3 flex flex-column p-3">
                            <div className="flex align-items-center justify-content-between mb-2">
                                <h4 className="mb-1">{shop.ShopName || shop.shopName}</h4>
                                <Tag 
                                    value={shop.ShopType || shop.shopType} 
                                    severity={getShopTypeSeverity(shop.ShopType)}
                                />
                            </div>
                            <div className="flex align-items-center mb-2">
                                <Rating value={shop.Rating || shop.rating || 0} readOnly cancel={false} />
                                <span className="ml-2 font-semibold">
                                    {(shop.Rating || shop.rating || 0).toFixed(1)} ({shop.TotalReviews || shop.totalReviews || 0} reviews)
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                                <i className="pi pi-map-marker mr-1"></i>
                                {shop.Address || shop.address}, {shop.City || shop.city}
                                {shop.distance > 0 && shop.distance < 999 && (
                                    <span className="ml-2 font-semibold">
                                        ({shop.distance.toFixed(1)} km away)
                                    </span>
                                )}
                            </div>
                            <div className="text-sm text-gray-600">
                                <i className="pi pi-phone mr-1"></i>
                                {shop.ContactPhone || shop.contactPhone} • 
                                <i className="pi pi-user ml-2 mr-1"></i>
                                {shop.OwnerName || shop.ownerName}
                            </div>
                        </div>
                        
                        <div className="w-full md:w-2 flex flex-column p-3">
                            <div className="grid">
                                <div className="col-6">
                                    <div className="font-bold text-lg">₹{shop.min_order_amt || shop.min_order_amt || 0}</div>
                                    <div className="text-sm text-gray-500">Min Order Amount</div>
                                </div>
                                <div className="col-6">
                                    <div className="font-bold text-lg">{shop.ServiceRadius || shop.serviceRadius || 0}km</div>
                                    <div className="text-sm text-gray-500">Service Radius</div>
                                </div>
                                <div className="col-6">
                                    <div className="font-bold text-lg">{shop.AvailableSlots || shop.availableSlots || 0}</div>
                                    <div className="text-sm text-gray-500">Available Slots</div>
                                </div>
                                <div className="col-6">
                                    <div className="font-bold text-lg">
                                        {shop.IsVerified ? 'Verified' : 'Pending'}
                                    </div>
                                    <div className="text-sm text-gray-500">Status</div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="w-full md:w-2 flex flex-column p-3 justify-content-center">
                            <Button 
                                label={isSelected ? "Selected" : "Select Shop"}
                                icon={isSelected ? "pi pi-check" : "pi pi-arrow-right"}
                                className={isSelected ? "p-button-success" : "p-button-outlined"}
                                onClick={() => setSelectedShop(shop)}
                            />
                        </div>
                    </div>
                </Card>
            </div>
        );
    };

    // ==================== PAYMENT RESPONSE DIALOG ====================

    const renderPaymentResponse = () => {
        if (!paymentResponse && paymentStatus !== 'failed') return null;
        
        return (
            <Dialog 
                header={paymentStatus === 'success' ? "Payment Successful!" : "Payment Failed"}
                visible={paymentStatus !== null} 
                style={{ width: '450px' }}
                onHide={() => {
                    setPaymentStatus(null);
                    if (paymentStatus === 'success') {
                        window.location.href = createdBookingId ? 
                            `/bookings/view/${createdBookingId}` : 
                            '/bookings/view';
                    }
                }}
                dismissableMask={paymentStatus === 'success'}
            >
                <div className="text-center">
                    {paymentStatus === 'success' ? (
                        <>
                            <i className="pi pi-check-circle" style={{ fontSize: '4rem', color: 'var(--green-500)' }}></i>
                            <h3 className="mt-3 mb-2">Payment Successful!</h3>
                            <p className="mb-3">Your payment has been processed successfully.</p>
                            
                            <div className="surface-50 p-3 border-round text-left mb-3">
                                <p className="mb-2"><strong>Transaction ID:</strong> {paymentResponse?.razorpay_payment_id}</p>
                                <p className="mb-2"><strong>Order ID:</strong> {paymentResponse?.razorpay_order_id}</p>
                                <p className="mb-0"><strong>Booking #:</strong> {createdBookingReference}</p>
                            </div>
                            
                            <p className="text-green-600 font-semibold">
                                Amount Paid: ₹{estimatedPrice.toFixed(2)}
                            </p>
                        </>
                    ) : (
                        <>
                            <i className="pi pi-times-circle" style={{ fontSize: '4rem', color: 'var(--red-500)' }}></i>
                            <h3 className="mt-3 mb-2">Payment Failed</h3>
                            <p className="mb-3 text-red-500">
                                Something went wrong with your payment.
                            </p>
                            
                            <div className="surface-50 p-3 border-round text-left mb-3">
                                <p className="mb-0">Your booking is created but payment is pending. You can complete the payment later from your bookings page.</p>
                            </div>
                            
                            <p className="text-gray-600 mb-4">
                                Booking #: {createdBookingReference}
                            </p>
                        </>
                    )}
                    
                    <div className="flex justify-content-center gap-2">
                        {paymentStatus === 'success' ? (
                            <>
                                <Button 
                                    label="View Booking" 
                                    icon="pi pi-list"
                                    className="p-button-outlined"
                                    onClick={() => window.location.href = createdBookingId ? 
                                        `/bookings/view/${createdBookingId}` : 
                                        '/bookings/view'
                                    }
                                />
                                <Button 
                                    label="Close" 
                                    icon="pi pi-times"
                                    className="p-button-secondary"
                                    onClick={() => {
                                        setPaymentStatus(null);
                                        window.location.href = '/bookings/listcustomer';
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Button 
                                    label="Try Again" 
                                    icon="pi pi-refresh"
                                    onClick={() => {
                                        setPaymentStatus(null);
                                        proceedToPayment();
                                    }}
                                />
                                <Button 
                                    label="View Booking" 
                                    icon="pi pi-list"
                                    className="p-button-outlined"
                                    onClick={() => window.location.href = createdBookingId ? 
                                        `/bookings/view/${createdBookingId}` : 
                                        '/bookings/view'
                                    }
                                />
                            </>
                        )}
                    </div>
                </div>
            </Dialog>
        );
    };

    // ==================== STEP RENDERERS ====================

    const renderServiceStep = () => (
        <Panel header="Select Service" toggleable>
            {services.length === 0 ? (
                <div className="text-center py-4">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    <p className="mt-3">Loading services...</p>
                </div>
            ) : (
                <div className="grid">
                    {services.map(service => {
                        const isSelected = bookingData.ServiceId === service.id;
                        const basePrice = service.baseprice || service.basePrice || 0;
                        const category = service.Category || service.category || 'Standard';
                        const estimatedHours = service.estimateddurationhours || service.estimatedDurationHours || 24;
                        
                        return (
                            <div className="col-12 md:col-6 lg:col-4" key={service.id}>
                                <Card 
                                    className={`cursor-pointer transition-all duration-200 hover:shadow-4 ${isSelected ? 'border-2 border-primary shadow-3 bg-blue-50' : 'border-1 border-gray-200'}`}
                                    onClick={() => handleInputChange('ServiceId', service.id)}
                                >
                                    <div className="text-center relative">
                                        {isSelected && (
                                            <div className="absolute top-3 right-3">
                                                <i className="pi pi-check-circle text-primary" style={{ fontSize: '1.5rem' }}></i>
                                            </div>
                                        )}
                                        
                                        <i className={`pi ${getServiceIcon(category)} ${getServiceColor(category)}`} style={{ 
                                            fontSize: '2.5rem'
                                        }}></i>
                                        
                                        <h4 className={`mt-3 mb-2 ${isSelected ? 'text-primary' : ''}`}>
                                            {service.name}
                                        </h4>
                                        
                                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                                            {service.description || 'Professional laundry service'}
                                        </p>
                                        
                                        <div className="flex justify-content-center align-items-center gap-2 mb-2">
                                            <Badge 
                                                value={`₹${basePrice}`} 
                                                severity={isSelected ? "success" : "info"}
                                                className={isSelected ? 'scale-110' : ''}
                                            />
                                            <Badge 
                                                value={category} 
                                                severity="contrast" 
                                                className="text-xs"
                                            />
                                        </div>
                                        
                                        <div className="flex justify-content-between text-sm text-gray-500 mt-3">
                                            <div className="flex align-items-center">
                                                <i className="pi pi-clock mr-1"></i>
                                                <span>{estimatedHours}h</span>
                                            </div>
                                            {service.isavailable === false && (
                                                <div className="flex align-items-center">
                                                    <i className="pi pi-times mr-1 text-red-500"></i>
                                                    <span className="text-red-500">Unavailable</span>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {category === 'Express' && (
                                            <div className="absolute top-2 left-2">
                                                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                                                    Fastest
                                                </span>
                                            </div>
                                        )}
                                        
                                        {category === 'Premium' && (
                                            <div className="absolute top-2 left-2">
                                                <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                                                    Premium
                                                </span>
                                            </div>
                                        )}
                                        
                                        <div className="mt-4">
                                            <Button 
                                                label={isSelected ? "Selected" : "Select Service"}
                                                icon={isSelected ? "pi pi-check" : "pi pi-arrow-right"}
                                                className={isSelected ? 
                                                    "p-button-success p-button-sm w-full" : 
                                                    "p-button-outlined p-button-sm w-full"
                                                }
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleInputChange('ServiceId', service.id);
                                                }}
                                                disabled={service.isavailable === false}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        );
                    })}
                </div>
            )}
            
            {bookingData.ServiceId && (
                <div className="mt-4 p-3 surface-100 border-round">
                    <div className="flex align-items-center justify-content-between">
                        <div className="flex align-items-center">
                            <i className="pi pi-check-circle text-primary mr-2" style={{ fontSize: '1.25rem' }}></i>
                            <span>
                                <strong>Selected:</strong> {
                                    services.find(s => s.id === bookingData.ServiceId)?.name || 'Unknown service'
                                }
                            </span>
                        </div>
                        <Button 
                            label="Change" 
                            icon="pi pi-refresh" 
                            className="p-button-text p-button-sm"
                            onClick={() => handleInputChange('ServiceId', null)}
                        />
                    </div>
                </div>
            )}
            
            {errors.ServiceId && (
                <Message severity="error" text={errors.ServiceId} className="mt-3" />
            )}
        </Panel>
    );

    const renderShopSelectionStep = () => (
        <Panel header="Select Partner Shop" toggleable>
            <div className="mb-4">
                <Card>
                    <div className="grid align-items-center">
                        <div className="col-12 md:col-4">
                            <div className="p-inputgroup">
                                <span className="p-inputgroup-addon">
                                    <i className="pi pi-search"></i>
                                </span>
                                <InputText 
                                    placeholder="Search shops by name, city, or code..." 
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="w-full"
                                />
                            </div>
                        </div>
                        
                        <div className="col-12 md:col-3">
                            <Dropdown 
                                value={selectedShopType}
                                options={shopTypeOptions}
                                onChange={(e) => setSelectedShopType(e.value)}
                                placeholder="Shop Type"
                                className="w-full"
                            />
                        </div>
                        
                        <div className="col-12 md:col-3">
                            <div className="flex align-items-center gap-3">
                                <label className="font-medium whitespace-nowrap">Max: {maxDistance}km</label>
                                <Slider 
                                    value={maxDistance}
                                    onChange={(e) => {
                                        setMaxDistance(e.value);
                                        setTimeout(() => filterShops(), 0);
                                    }}
                                    min={1}
                                    max={50}
                                    className="w-full"
                                />
                            </div>
                        </div>
                        
                        <div className="col-12 md:col-2">
                            <div className="flex justify-content-end">
                                <Button 
                                    icon={shopLayout === 'grid' ? 'pi pi-list' : 'pi pi-th-large'}
                                    onClick={() => setShopLayout(shopLayout === 'grid' ? 'list' : 'grid')}
                                    className="p-button-outlined"
                                    tooltip="Toggle View"
                                />
                            </div>
                        </div>
                    </div>
                    
                    {!userLocation && (
                        <div className="mt-3 p-2 bg-warning-50 border-round">
                            <i className="pi pi-exclamation-triangle mr-2 text-warning"></i>
                            <span className="text-sm">Getting your location to show nearby shops...</span>
                        </div>
                    )}
                    
                    {userLocation && userLocation.lat !== 40.7128 && (
                        <div className="mt-3 p-2 bg-info-50 border-round">
                            <i className="pi pi-map-marker mr-2 text-primary"></i>
                            <span className="text-sm">
                                Showing shops within {maxDistance}km of your location
                                {filteredShops.length === 0 && (
                                    <span className="text-sm text-warning ml-2">
                                        - No shops found. Try increasing the radius above.
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                    
                    <div className="grid mt-4">
                        <div className="col-12 md:col-3">
                            <Card className="shadow-2 border-round-lg">
                                <div className="flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-2xl font-bold text-primary">
                                            {filteredShops.length}
                                        </div>
                                        <div className="text-color-secondary">
                                            Available Shops
                                        </div>
                                    </div>
                                    <i className="pi pi-gift text-4xl text-primary opacity-50"></i>
                                </div>
                            </Card>
                        </div>

                        <div className="col-12 md:col-3">
                            <Card className="shadow-2 border-round-lg">
                                <div className="flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-2xl font-bold text-green-500">
                                            {filteredShops.filter(s => s.Status === 'Active').length}
                                        </div>
                                        <div className="text-color-secondary">
                                            Active
                                        </div>
                                    </div>
                                    <i className="pi pi-check-circle text-4xl text-green-400 opacity-50"></i>
                                </div>
                            </Card>
                        </div>

                        <div className="col-12 md:col-3">
                            <Card className="shadow-2 border-round-lg">
                                <div className="flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-2xl font-bold text-yellow-500">
                                            {filteredShops.filter(s => s.IsVerified).length}
                                        </div>
                                        <div className="text-color-secondary">
                                            Verified
                                        </div>
                                    </div>
                                    <i className="pi pi-shield text-4xl text-yellow-400 opacity-50"></i>
                                </div>
                            </Card>
                        </div>

                        <div className="col-12 md:col-3">
                            <Card className="shadow-2 border-round-lg">
                                <div className="flex justify-content-between align-items-center">
                                    <div>
                                        <div className="text-2xl font-bold text-purple-500">
                                            {filteredShops.reduce((sum, shop) => 
                                                sum + (shop.TotalReviews || shop.totalReviews || 0), 0
                                            )}
                                        </div>
                                        <div className="text-color-secondary">
                                            Total Reviews
                                        </div>
                                    </div>
                                    <i className="pi pi-star text-4xl text-purple-400 opacity-50"></i>
                                </div>
                            </Card>
                        </div>
                    </div>
                </Card>
            </div>

            {isLoadingShops ? (
                <div className="text-center py-8">
                    <ProgressSpinner style={{ width: '60px', height: '60px' }} />
                    <p className="mt-3">Loading available shops...</p>
                </div>
            ) : filteredShops.length === 0 ? (
                <div className="text-center p-6">
                    <i className="pi pi-search text-4xl text-gray-400 mb-3"></i>
                    <h3>No shops available</h3>
                    <p className="text-gray-600 mb-4">
                        {userLocation && userLocation.lat !== 40.7128 
                            ? `No laundry shops found within ${maxDistance}km of your location. Try increasing the search radius above.`
                            : 'Try adjusting your filters or expanding the search radius'}
                    </p>
                    <Button 
                        label="Reload Shops" 
                        icon="pi pi-refresh" 
                        className="p-button-outlined"
                        onClick={loadPartnerShops}
                    />
                </div>
            ) : (
                <>
                    {shopLayout === 'grid' ? (
                        <div className="grid">
                            {filteredShops.map(shop => shopGridItem(shop))}
                        </div>
                    ) : (
                        <div className="grid">
                            {filteredShops.map(shop => shopListItem(shop))}
                        </div>
                    )}
                </>
            )}
            
            {errors.shop && (
                <Message severity="error" text={errors.shop} className="mt-3" />
            )}
            
            {selectedShop && (
                <div className="mt-4 p-3 surface-100 border-round">
                    <div className="flex align-items-center justify-content-between">
                        <div className="flex align-items-center">
                            <i className="pi pi-check-circle text-primary mr-2" style={{ fontSize: '1.25rem' }}></i>
                            <span>
                                <strong>Selected Shop:</strong> {selectedShop.ShopName || selectedShop.shopName} - {selectedShop.City || selectedShop.city}
                                {selectedShop.distance > 0 && selectedShop.distance < 999 && ` (${selectedShop.distance.toFixed(1)} km away)`}
                            </span>
                        </div>
                        <Button 
                            label="Change" 
                            icon="pi pi-refresh" 
                            className="p-button-text p-button-sm"
                            onClick={() => setSelectedShop(null)}
                        />
                    </div>
                </div>
            )}
        </Panel>
    );

    const renderItemsStep = () => (
        <Panel header="Add Items" toggleable>
            {!selectedShop && (
                <Message 
                    severity="warn" 
                    text="Please select a shop first to see available items" 
                    className="mb-3"
                    icon="pi pi-info-circle"
                />
            )}
            
            {selectedShop && filteredItemTypes.length > 0 && (
                <div className="mb-3 p-2 bg-blue-50 border-round">
                    <div className="flex align-items-center justify-content-between">
                        <div>
                            <i className="pi pi-store mr-2 text-primary"></i>
                            <strong>{selectedShop.ShopName || selectedShop.shopName}</strong> offers 
                            <Badge value={`${filteredItemTypes.length} items`} className="ml-2" severity="info" />
                            {treatments.length > 0 && (
                                <Badge value={`${treatments.length} treatments`} className="ml-2" severity="success" />
                            )}
                        </div>
                        <Button 
                            label="Change Shop" 
                            icon="pi pi-arrow-left" 
                            className="p-button-text p-button-sm"
                            onClick={() => setActiveStep(1)}
                        />
                    </div>
                </div>
            )}
            
            {selectedShop && isLoadingTreatments && (
                <div className="text-center py-2 mb-3">
                    <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                    <span className="ml-2">Loading treatments for this shop...</span>
                </div>
            )}
            
            <div className="mb-3">
                <Button 
                    label="Add Item" 
                    icon="pi pi-plus" 
                    className="p-button-outlined p-button-sm"
                    onClick={addItem}
                    disabled={filteredItemTypes.length === 0 || isLoadingTreatments}
                />
                {filteredItemTypes.length === 0 && selectedShop && (
                    <small className="p-error block mt-1">No items available for this shop. Please select a different shop.</small>
                )}
                {isLoadingTreatments && (
                    <small className="p-info block mt-1">Loading treatments, please wait...</small>
                )}
            </div>

            {filteredItemTypes.length === 0 ? (
                <div className="text-center py-4">
                    {!selectedShop ? (
                        <>
                            <i className="pi pi-store text-4xl text-gray-400 mb-3"></i>
                            <p>Please select a shop first to see available items</p>
                            <Button 
                                label="Go to Shop Selection" 
                                icon="pi pi-arrow-right" 
                                className="p-button-primary mt-2"
                                onClick={() => setActiveStep(1)}
                            />
                        </>
                    ) : (
                        <>
                            <i className="pi pi-exclamation-triangle text-4xl text-warning mb-3"></i>
                            <p>No items available for this shop</p>
                            <Button 
                                label="Select Another Shop" 
                                icon="pi pi-refresh" 
                                className="p-button-outlined mt-2"
                                onClick={() => setActiveStep(1)}
                            />
                        </>
                    )}
                </div>
            ) : (
                <>
                    <DataTable value={bookingData.items} size="small" className="p-datatable-sm">
                       <Column 
    header="Item" 
    body={(rowData, { rowIndex }) => (
        <div>
            <Dropdown
                value={rowData.itemtype}
                options={filteredItemTypes.map(item => ({
                    label: item.itemtype,
                    value: item.itemtype,
                    category: item.category,
                    basePrice: item.baseprice,
                    itemid: item.itemid
                }))}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => handleArrayChange(rowIndex, 'itemtype', e.value)}
                placeholder="Select Item"
                className="w-full"
                filter
            />
            {errors[`itemType_${rowIndex}`] && (
                <small className="p-error block mt-1">{errors[`itemType_${rowIndex}`]}</small>
            )}
        </div>
    )} 
/>

                        <Column 
                            header="Qty" 
                            body={(rowData, { rowIndex }) => (
                                <div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            icon="pi pi-minus"
                                            onClick={() => {
                                                const newValue = Math.max(1, (rowData.quantity || 1) - 1);
                                                handleArrayChange(rowIndex, 'quantity', newValue);
                                            }}
                                            disabled={rowData.quantity <= 1}
                                            size="small"
                                            text
                                            rounded
                                        />
                                        <InputText
                                            type="number"
                                            value={rowData.quantity}
                                            onChange={(e) => {
                                                const newValue = parseInt(e.target.value) || 1;
                                                handleArrayChange(rowIndex, 'quantity', newValue);
                                            }}
                                            className="w-full text-center"
                                            min="1"
                                            max={filteredItemTypes.find(it => 
                                                (it.Itemtype || it.itemtype) === rowData.itemtype
                                            )?.MaxQuantityPerBooking || 99}
                                        />
                                        <Button
                                            icon="pi pi-plus"
                                            onClick={() => {
                                                const maxValue = filteredItemTypes.find(it => 
                                                    (it.Itemtype || it.itemtype) === rowData.itemtype
                                                )?.MaxQuantityPerBooking || 99;
                                                const newValue = Math.min(maxValue, (rowData.quantity || 1) + 1);
                                                handleArrayChange(rowIndex, 'quantity', newValue);
                                            }}
                                            disabled={rowData.quantity >= (filteredItemTypes.find(it => 
                                                (it.Itemtype || it.itemtype) === rowData.itemtype
                                            )?.MaxQuantityPerBooking || 99)}
                                            size="small"
                                            text
                                            rounded
                                        />
                                    </div>
                                    {errors[`quantity_${rowIndex}`] && (
                                        <small className="p-error block mt-1">{errors[`quantity_${rowIndex}`]}</small>
                                    )}
                                </div>
                            )} 
                        />

                       <Column 
    header="Treatment" 
    body={(rowData, { rowIndex }) => {
        // Get treatments filtered by the selected item type
        const selectedItemType = filteredItemTypes.find(it => 
            it.itemtype === rowData.itemtype
        );
        
        // Get the itemid from the selected item type
        const itemTypeId = selectedItemType?.itemid;
        
        console.log('Selected Item Type:', rowData.itemtype, 'Item Type ID (itemid):', itemTypeId);
        console.log('All Treatments:', treatments);
        console.log('Filtering treatments with itemTypeId:', itemTypeId);
        
        // Filter treatments by itemTypeId (treatments use itemTypeId field)
        const filteredTreatments = treatments.filter(t => {
            // Compare the itemTypeId from treatment with the itemid from item type
            return t.itemTypeId === itemTypeId;
        });
        
        // Group treatments to show unique treatment names
        const uniqueTreatments = filteredTreatments.reduce((acc, curr) => {
            if (!acc.find(item => item.treatment === curr.treatment)) {
                acc.push(curr);
            }
            return acc;
        }, []);
        
        console.log('Unique Treatments for this item type:', uniqueTreatments);
        
        return (
            <div>
                <Dropdown
                    value={rowData.treatment}
                    options={uniqueTreatments.map(treatment => ({
                        label: treatment.treatment,
                        value: treatment.treatment,
                        priceMultiplier: treatment.priceMultiplier || 1.0
                    }))}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => handleArrayChange(rowIndex, 'treatment', e.value)}
                    placeholder={!rowData.itemtype ? "Select item first" : 
                                isLoadingTreatments ? "Loading treatments..." : 
                                uniqueTreatments.length === 0 ? "No treatments available" : 
                                "Select Treatment"}
                    className="w-full"
                    disabled={!rowData.itemtype || isLoadingTreatments || uniqueTreatments.length === 0}
                />
                {!rowData.itemtype && (
                    <small className="p-info block mt-1">Please select an item type first</small>
                )}
                {rowData.itemtype && uniqueTreatments.length === 0 && !isLoadingTreatments && (
                    <small className="p-warn block mt-1">No treatments available for this item</small>
                )}
                {isLoadingTreatments && (
                    <small className="p-info block mt-1">Loading treatments...</small>
                )}
            </div>
        );
    }} 
/>

                        <Column 
                            header="Unit Price" 
                            body={(rowData) => `₹${(rowData.unitprice || 0).toFixed(2)}`}
                        />
                        <Column 
                            header="Total" 
                            body={(rowData) => `₹${(rowData.totalprice || 0).toFixed(2)}`}
                        />
                        <Column 
                            header="Action" 
                            body={(rowData) => (
                                <Button 
                                    icon="pi pi-trash" 
                                    className="p-button-danger p-button-text p-button-sm"
                                    onClick={() => removeItem(rowData.id)}
                                    disabled={bookingData.items.length === 1}
                                    tooltip="Remove item"
                                />
                            )} 
                        />
                    </DataTable>

                    {errors.items && (
                        <Message severity="error" text={errors.items} className="mt-3" />
                    )}

                    <Divider />
                    <div className="flex justify-content-between align-items-center mt-3">
                        <div>
                            <span className="text-gray-600">Total Items: </span>
                            <span className="font-bold">{bookingData.items.reduce((sum, item) => sum + (item.quantity || 0), 0)}</span>
                        </div>
                        <div>
                            <span className="text-gray-600 mr-2">Estimated Total:</span>
                            <span className="font-bold text-xl text-primary">
                                ₹{(isNaN(estimatedPrice) ? 0 : estimatedPrice).toFixed(2)}
                            </span>
                        </div>
                    </div>
                </>
            )}
        </Panel>
    );

    const renderDetailsStep = () => (
        <Panel header="Pickup & Delivery Details" toggleable>
            <div className="grid">
                <div className="col-12 md:col-6">
                    <div className="field">
                        <label className="block mb-2 font-medium">
                            Pickup Date <span className="text-red-500">*</span>
                        </label>
                        <Calendar
                            value={bookingData.PickupDate}
                            onChange={(e) => handleInputChange('PickupDate', e.value)}
                            dateFormat="dd/mm/yy"
                            showTime
                            hourFormat="12"
                            className={`w-full ${errors.PickupDate ? 'p-invalid' : ''}`}
                            minDate={new Date()}
                            maxDate={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                        />
                        {errors.PickupDate && (
                            <small className="p-error block mt-1">{errors.PickupDate}</small>
                        )}
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field">
                        <label className="block mb-2 font-medium">
                            Delivery Date <span className="text-red-500">*</span>
                        </label>
                        <Calendar
                            value={bookingData.DeliveryDate}
                            onChange={(e) => handleInputChange('DeliveryDate', e.value)}
                            dateFormat="dd/mm/yy"
                            showTime
                            hourFormat="12"
                            minDate={bookingData.PickupDate}
                            maxDate={new Date(bookingData.PickupDate.getTime() + 7 * 24 * 60 * 60 * 1000)}
                            className={`w-full ${errors.DeliveryDate ? 'p-invalid' : ''}`}
                        />
                        {errors.DeliveryDate && (
                            <small className="p-error block mt-1">{errors.DeliveryDate}</small>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mt-3">
                        <label className="block mb-2 font-medium">
                            Pickup Address <span className="text-red-500">*</span>
                        </label>
                        <div className="p-inputgroup">
                            <InputTextarea
                                value={bookingData.PickupAddress}
                                onChange={(e) => handleInputChange('PickupAddress', e.target.value)}
                                rows={3}
                                className={`w-full ${errors.PickupAddress ? 'p-invalid' : ''}`}
                                placeholder="Enter complete pickup address including street, city, and postal code"
                            />
                            <Button
                                icon={gettingPickupAddress ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
                                onClick={() => getCurrentLocationAddress('pickup')}
                                disabled={gettingPickupAddress}
                                className="p-button-outlined"
                                tooltip="Use current location"
                                style={{ height: 'auto' }}
                            />
                        </div>
                        {errors.PickupAddress && (
                            <small className="p-error block mt-1">{errors.PickupAddress}</small>
                        )}
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mt-3">
                        <label className="block mb-2 font-medium">Delivery Address</label>
                        <div className="p-inputgroup">
                            <InputTextarea
                                value={bookingData.DeliveryAddress}
                                onChange={(e) => handleInputChange('DeliveryAddress', e.target.value)}
                                rows={3}
                                className="w-full"
                                placeholder="Enter delivery address (leave blank if same as pickup)"
                            />
                            <Button
                                icon={gettingDeliveryAddress ? "pi pi-spin pi-spinner" : "pi pi-map-marker"}
                                onClick={() => getCurrentLocationAddress('delivery')}
                                disabled={gettingDeliveryAddress}
                                className="p-button-outlined"
                                tooltip="Use current location"
                                style={{ height: 'auto' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className="field mt-3">
                        <label className="block mb-2 font-medium">Special Instructions</label>
                        <InputTextarea
                            value={bookingData.SpecialInstructions}
                            onChange={(e) => handleInputChange('SpecialInstructions', e.target.value)}
                            rows={2}
                            className="w-full"
                            placeholder="Any special requirements or instructions for pickup/delivery..."
                        />
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field mt-3">
                        <label className="block mb-2 font-medium">Preferred Contact Method</label>
                        <Dropdown
                            value={bookingData.PreferredContactMethod}
                            options={[
                                { label: 'Phone', value: 'Phone' },
                                { label: 'Email', value: 'Email' },
                                { label: 'SMS', value: 'SMS' },
                                { label: 'WhatsApp', value: 'WhatsApp' }
                            ]}
                            onChange={(e) => handleInputChange('PreferredContactMethod', e.value)}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="col-12 md:col-6">
                    <div className="field mt-3">
                        <label className="block mb-2 font-medium">Priority</label>
                        <div className="flex align-items-center">
                            <Checkbox
                                inputId="urgent"
                                checked={bookingData.IsUrgent}
                                onChange={(e) => handleInputChange('IsUrgent', e.checked)}
                                className="mr-2"
                            />
                            <label htmlFor="urgent" className="cursor-pointer">
                                Mark as Urgent (Extra charges may apply)
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </Panel>
    );

    const renderConfirmStep = () => {
        const selectedItemTypeName = (itemType) => {
            const item = filteredItemTypes.find(it => 
                (it.Itemtype || it.itemtype) === itemType
            );
            return item?.Itemtype || itemType || 'Unknown';
        };
        
        const selectedTreatmentName = (treatment) => {
            const treat = treatments.find(t => 
                t.treatment === treatment
            );
            return treat?.treatment || treatment || 'Standard';
        };
        
        const serviceBasePrice = selectedService?.baseprice || selectedService?.basePrice || 0;
        
        return (
            <Panel header="Review & Confirm" toggleable>
                <div className="grid">
                    <div className="col-12 md:col-6">
                        <h4 className="mt-0">Booking Summary</h4>
                        <div className="surface-50 p-3 border-round">
                            <p><strong>Service:</strong> {selectedService?.name}</p>
                            <p><strong>Category:</strong> {selectedService?.Category || selectedService?.category || 'Standard'}</p>
                            <p><strong>Status:</strong> <Badge value={bookingData.Status} severity="info" /></p>
                            <p><strong>Pickup:</strong> {new Date(bookingData.PickupDate).toLocaleString()}</p>
                            <p><strong>Delivery:</strong> {new Date(bookingData.DeliveryDate).toLocaleString()}</p>
                            <p><strong>Pickup Address:</strong> {bookingData.PickupAddress}</p>
                            {selectedShop && (
                                <p><strong>Assigned Shop:</strong> {selectedShop.ShopName || selectedShop.shopName} ({selectedShop.City || selectedShop.city})</p>
                            )}
                            <p><strong>Contact Method:</strong> {bookingData.PreferredContactMethod}</p>
                            {bookingData.IsUrgent && (
                                <p><strong>Priority:</strong> <Badge value="URGENT" severity="danger" /></p>
                            )}
                            
                            <div className="mt-3">
                                <strong>Items ({bookingData.items.reduce((sum, item) => sum + (item.quantity || 0), 0)} total):</strong>
                                <ul className="mt-1 list-none p-0">
                                    {bookingData.items.map((item, index) => (
                                        <li key={index} className="mb-1">
                                            {item.quantity}x {selectedItemTypeName(item.itemtype)} 
                                            {item.treatment && ` - ${selectedTreatmentName(item.treatment)}`}
                                            <span className="float-right">₹{(item.totalprice || 0).toFixed(2)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            
                            {bookingData.SpecialInstructions && (
                                <div className="mt-3">
                                    <strong>Special Instructions:</strong>
                                    <p className="mt-1 text-sm">{bookingData.SpecialInstructions}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-12 md:col-6">
                        <h4 className="mt-0">Price Breakdown</h4>
                        <div className="surface-50 p-3 border-round">
                            <div className="flex justify-content-between mb-2">
                                <span>Base Service Charge:</span>
                                <span>₹{serviceBasePrice.toFixed(2)}</span>
                            </div>
                            
                            {bookingData.items.map((item, index) => (
                                <div key={index} className="flex justify-content-between mb-1 text-sm">
                                    <span>
                                        {item.quantity}x {selectedItemTypeName(item.itemtype)}
                                        {item.treatment && ` (${selectedTreatmentName(item.treatment)})`}
                                    </span>
                                    <span>₹{(item.totalprice || 0).toFixed(2)}</span>
                                </div>
                            ))}
                            
                            {bookingData.WeightCharge > 0 && (
                                <div className="flex justify-content-between mb-2">
                                    <span>Weight Charge:</span>
                                    <span>₹{bookingData.WeightCharge.toFixed(2)}</span>
                                </div>
                            )}
                            
                            {bookingData.ExtraCharges > 0 && (
                                <div className="flex justify-content-between mb-2">
                                    <span>Extra Charges:</span>
                                    <span>₹{bookingData.ExtraCharges.toFixed(2)}</span>
                                </div>
                            )}
                            
                            {bookingData.DiscountAmount > 0 && (
                                <div className="flex justify-content-between mb-2 text-green-600">
                                    <span>Discount:</span>
                                    <span>-₹{bookingData.DiscountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            
                            <Divider />
                            
                            <div className="flex justify-content-between mb-2">
                                <span>Subtotal:</span>
                                <span>₹{(serviceBasePrice + bookingData.items.reduce((sum, item) => sum + (item.totalprice || 0), 0)).toFixed(2)}</span>
                            </div>
                            
                            <div className="flex justify-content-between mb-2">
                                <span>Tax ({(bookingData.TaxRate * 100).toFixed(0)}%):</span>
                                <span>₹{bookingData.TaxAmount.toFixed(2)}</span>
                            </div>
                            
                            <Divider />
                            <div className="flex justify-content-between align-items-center mt-3 pt-2">
                                <span className="font-bold text-lg">Total Amount:</span>
                                <span className="text-xl font-bold text-primary">₹{estimatedPrice.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="field-checkbox mt-4">
                    <Checkbox 
                        inputId="terms" 
                        checked={bookingData.acceptTerms} 
                        onChange={(e) => handleInputChange('acceptTerms', e.checked)}
                        className={errors.acceptTerms ? 'p-invalid' : ''}
                    />
                    <label htmlFor="terms" className="ml-2 cursor-pointer">
                        I accept the terms and conditions, and understand that cancellation fees may apply
                    </label>
                    {errors.acceptTerms && (
                        <small className="p-error block mt-1">{errors.acceptTerms}</small>
                    )}
                </div>
            </Panel>
        );
    };

    const renderSuccessDialog = () => (
        <Dialog 
            header="Booking Created!" 
            visible={showSuccess} 
            style={{ width: '500px' }}
            onHide={() => setShowSuccess(false)}
            dismissableMask
        >
            <div className="text-center">
                <i className="pi pi-check-circle" style={{ fontSize: '4rem', color: 'var(--green-500)' }}></i>
                <h3 className="mt-3 mb-2">Booking Created Successfully!</h3>
                <p className="mb-3">Your laundry booking has been confirmed.</p>
                
                <div className="surface-50 p-3 border-round text-left mb-4">
                    <p className="mb-2">
                        <strong>Booking #:</strong> {createdBookingReference}
                    </p>
                    <p className="mb-2">
                        <strong>Service:</strong> {selectedService?.name}
                    </p>
                    <p className="mb-2">
                        <strong>Shop:</strong> {selectedShop?.ShopName || selectedShop?.shopName}
                    </p>
                    <p className="mb-2">
                        <strong>Pickup:</strong> {formatDateTime(bookingData.PickupDate)}
                    </p>
                    <p className="mb-0">
                        <strong>Amount:</strong> <span className="text-xl font-bold text-primary">₹{estimatedPrice.toFixed(2)}</span>
                    </p>
                    <p className="mb-0 mt-2">
                        <strong>Payment Status:</strong> <Badge value="Pending" severity="warning" />
                    </p>
                </div>
                
                <div className="mb-4 p-3 border-1 border-round border-green-200 bg-green-50">
                    <div className="flex align-items-center justify-content-between">
                        <div className="flex align-items-center">
                            <i className="pi pi-whatsapp text-2xl text-green-600 mr-2"></i>
                            <div className="text-left">
                                <h4 className="m-0 text-green-700">WhatsApp Confirmation</h4>
                                <p className="text-sm text-gray-600 m-0">Invoice sent to your WhatsApp</p>
                            </div>
                        </div>
                        {whatsappSent && (
                            <i className="pi pi-check-circle text-green-600 text-xl"></i>
                        )}
                    </div>
                    {whatsappSent && (
                        <div className="flex align-items-center mt-2 text-green-600">
                            <i className="pi pi-check-circle mr-1"></i>
                            <small>WhatsApp confirmation sent successfully!</small>
                        </div>
                    )}
                </div>
                
                <div className="flex justify-content-center gap-2 flex-wrap">
                    <Button 
                        label="Pay Now" 
                        icon="pi pi-credit-card"
                        className="p-button-success"
                        onClick={proceedToPayment}
                        loading={paymentLoading}
                        disabled={!createdBookingId}
                    />
                    <Button 
                        label="Pay Later" 
                        icon="pi pi-clock"
                        className="p-button-outlined"
                        onClick={() => {
                            setShowSuccess(false);
                            window.location.href = createdBookingId ? 
                                `/bookings/view/${createdBookingId}` : 
                                '/bookings/view';
                        }}
                    />
                    <Button 
                        label="View Booking" 
                        icon="pi pi-list"
                        className="p-button-outlined p-button-secondary"
                        onClick={() => window.location.href = createdBookingId ? 
                            `/bookings/listcustomer/${createdBookingId}` : 
                            '/bookings/listcustomer'
                        }
                    />
                </div>
                
                <Divider />
                
                <div className="text-left text-sm text-gray-500">
                    <p className="mb-1"><i className="pi pi-whatsapp mr-1 text-green-600"></i> A WhatsApp confirmation has been sent with payment pending status.</p>
                    <p className="mb-0"><i className="pi pi-credit-card mr-1"></i> You can complete the payment now or later from your bookings page.</p>
                </div>
            </div>
        </Dialog>
    );

    const renderStepContent = () => {
        switch(activeStep) {
            case 0: return renderServiceStep();
            case 1: return renderShopSelectionStep();
            case 2: return renderItemsStep();
            case 3: return renderDetailsStep();
            case 4: return renderConfirmStep();
            default: return null;
        }
    };

    // ==================== MAIN RENDER ====================

    return (
        <div className="p-fluid">
            <Toast ref={toast} position="top-right" />
            
            {renderSuccessDialog()}

            <Dialog 
                header="Processing Payment" 
                visible={paymentDialog} 
                style={{ width: '350px' }}
                modal
                closable={false}
            >
                <div className="text-center p-4">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                    <p className="mt-3">Please complete the payment in the popup window...</p>
                    <p className="text-sm text-gray-500">Do not close this window</p>
                </div>
            </Dialog>

            {renderPaymentResponse()}

            <Card title="Quick Laundry Booking" className="shadow-2">
                <div className="mb-4">
                    <Steps 
                        model={steps} 
                        activeIndex={activeStep}
                        onSelect={(e) => setActiveStep(e.index)}
                        readOnly={false}
                    />
                </div>

                {renderStepContent()}

                <div className="flex justify-content-between mt-4">
                    <Button
                        label="Back"
                        icon="pi pi-arrow-left"
                        className="p-button-text"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                    />

                    {activeStep < steps.length - 1 ? (
                        <Button
                            label="Next"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            onClick={handleNext}
                            disabled={loading || (activeStep === 1 && filteredShops.length === 0)}
                        />
                    ) : (
                        <Button
                            label={loading ? 'Creating Booking...' : 'Create Booking'}
                            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                            onClick={handleSubmit}
                            disabled={!bookingData.acceptTerms || loading}
                            loading={loading}
                            className="p-button-success"
                        />
                    )}
                </div>
            </Card>
        </div>
    );
};

export default BookingQuickForm;